import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Loader2, Trophy, Heart, Share2 } from "lucide-react";

export const Leaderboard = () => {
  const { t } = useI18n();
  const [period, setPeriod] = useState("7d");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/community/leaderboard/${period}`, { params: { limit: 5 } })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <aside className="surface-card p-5 space-y-4" data-testid="leaderboard">
      <div className="flex items-center justify-between">
        <span className="label-mono inline-flex items-center gap-1.5">
          <Trophy className="h-3 w-3 text-warn" strokeWidth={2} />
          {t("leaderboard_title")}
        </span>
      </div>
      <p className="text-xs text-neutral-500 leading-relaxed -mt-1">{t("leaderboard_subtitle")}</p>
      <Tabs value={period} onValueChange={setPeriod}>
        <TabsList className="grid grid-cols-2 bg-[#0e0e0e] border border-white/10 rounded-none h-9 p-0" data-testid="leaderboard-tabs">
          <TabsTrigger value="7d" className="rounded-none font-mono text-xs data-[state=active]:bg-bull data-[state=active]:text-black" data-testid="leaderboard-tab-7d">
            {t("leaderboard_period_7d")}
          </TabsTrigger>
          <TabsTrigger value="30d" className="rounded-none font-mono text-xs data-[state=active]:bg-bull data-[state=active]:text-black" data-testid="leaderboard-tab-30d">
            {t("leaderboard_period_30d")}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-bull" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-neutral-500 font-mono py-3" data-testid="leaderboard-empty">{t("leaderboard_empty")}</p>
      ) : (
        <ol className="space-y-2" data-testid="leaderboard-list">
          {rows.map((r, i) => (
            <li
              key={r.user_id}
              className="flex items-center gap-3 px-2 py-2 border border-white/5 bg-white/[0.02]"
              data-testid={`leaderboard-row-${r.user_id}`}
            >
              <span className={`font-mono text-xs w-5 text-center ${i === 0 ? "text-warn" : i === 1 ? "text-neutral-300" : i === 2 ? "text-bull/70" : "text-neutral-500"}`}>
                {i + 1}
              </span>
              <span className="h-7 w-7 inline-flex items-center justify-center border border-white/10 bg-white/[0.04] text-bull font-mono text-xs">
                {r.author_initial}
              </span>
              <span className="font-mono text-xs text-neutral-200 truncate flex-1">{r.author_name}</span>
              <span className="font-mono text-[11px] inline-flex items-center gap-1 text-bear" title="likes">
                <Heart className="h-3 w-3" strokeWidth={2} fill="currentColor" /> {r.total_likes}
              </span>
              <span className="font-mono text-[10px] inline-flex items-center gap-0.5 text-neutral-500" title={t("leaderboard_shared_count")}>
                <Share2 className="h-2.5 w-2.5" strokeWidth={2} /> {r.shared_count}
              </span>
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
};
